/* Capacidades opcionais do runtime cliente.
   No desktop, a bridge do preload usa as APIs nativas; na web, os helpers
   degradam para as APIs padrão do browser sem importar Electron neste pacote. */

interface NativeClientRuntime {
  readonly writeClipboardText?: (text: string) => void | Promise<void>;
  readonly createUuid?: () => string;
}

interface ClientGlobal {
  readonly planuze?: NativeClientRuntime;
}

const UUID_BYTE_LENGTH = 16;
const UUID_VERSION_INDEX = 6;
const UUID_VARIANT_INDEX = 8;
const UUID_VERSION_MASK = 0x0f;
const UUID_VERSION_4 = 0x40;
const UUID_VARIANT_MASK = 0x3f;
const UUID_VARIANT_RFC_4122 = 0x80;
const HEX_RADIX = 16;

function nativeRuntime(): NativeClientRuntime | undefined {
  return (globalThis as typeof globalThis & ClientGlobal).planuze;
}

/** Copia texto sem propagar a indisponibilidade da área de transferência para a UI. */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    const bridge = nativeRuntime();
    if (typeof bridge?.writeClipboardText === 'function') {
      await bridge.writeClipboardText(text);
      return true;
    }

    const clipboard = globalThis.navigator?.clipboard;
    if (typeof clipboard?.writeText !== 'function') return false;
    await clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Cria UUID v4 somente a partir de fontes de aleatoriedade seguras. */
export function createClientUuid(): string {
  const bridge = nativeRuntime();
  if (typeof bridge?.createUuid === 'function') {
    try {
      return bridge.createUuid();
    } catch {
      /* A API web segura abaixo ainda pode estar disponível. */
    }
  }

  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  if (typeof cryptoApi?.getRandomValues !== 'function') {
    throw new Error('secure_random_unavailable');
  }

  const bytes = new Uint8Array(UUID_BYTE_LENGTH);
  cryptoApi.getRandomValues(bytes);
  const versionByte = bytes[UUID_VERSION_INDEX] ?? 0;
  const variantByte = bytes[UUID_VARIANT_INDEX] ?? 0;
  bytes[UUID_VERSION_INDEX] = (versionByte & UUID_VERSION_MASK) | UUID_VERSION_4;
  bytes[UUID_VARIANT_INDEX] = (variantByte & UUID_VARIANT_MASK) | UUID_VARIANT_RFC_4122;
  const hex = Array.from(bytes, (byte) => byte.toString(HEX_RADIX).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
